import { MODULE_EVIDENCE_CONTRACT_REGISTRY } from './contracts';
import { listLeverOverlapDeclarations } from './lever-overlap-registry';
import {
  GOVERNED_METRIC_REGISTRY_COMPILED_DEFINITIONS,
  resolveMetricTargetReference,
} from './metric-registry';
import { resolveObjectiveMetricIdentity } from './objective-metric-identity';
import type { LoadedObjectiveTaxonomyConfig } from './objective-taxonomy-resolver';
import {
  TARGET_METRIC_REGISTRY,
  TARGET_METRIC_REGISTRY_VERSION,
} from './ask-helix/contracts/target-metric-registry';

// Governed semantic graph — Phase 6 of the graph completion program, delivered the
// only way that program allows: as a governed READ VIEW, never a second source of
// truth (docs/process/graph-completion-program-20260807.md, Phase 6).
//
// SCOPE (deliberate): this is the STATIC-AUTHORITY layer of the graph — the
// capability catalog projected from registries and approved config, identical for
// every tenant. Tenant-scoped runtime state (StrategicObjectiveBinding rows,
// governed facts, ledger decisions, drift state) is a separate overlay with its own
// named trigger; the drift catalog's governedMetricResolution field is the first
// runtime seam already joined to this namespace. Callers must not present this
// projection as a statement about any tenant's LIVE bindings or facts.
//
// Every node and edge in this projection is DERIVED from a structure the product
// already governs, and each carries a `sourceRef` naming the exact governed source it
// was read from:
//
//   objective            objective-taxonomy-map.json (per-stack mapping entries)
//   metric               compiled governed metric registry (metric-registry.ts)
//   target_family        TARGET_METRIC_REGISTRY (economic family / sign semantics)
//   lever_family         taxonomy overlays (core / industry / tenant)
//   module               module-binding overlays (lever-module bindings)
//   evidence             binding requiredEvidence + MODULE_EVIDENCE_CONTRACT_REGISTRY
//   rollup_formula       registry aggregationRules (ruleFormulaId vocabulary)
//
// The projection is TOTAL and HONEST: a hop that cannot be id-resolved is never
// guessed, dropped, or suffix-matched — it is recorded in `unlinked` with the exact
// subject and reason, so the graph shows the true governed state including its gaps.
// (Conformance pins which gaps are the known, deliberate ones.)
//
// This module is pure: it takes the loaded taxonomy config as input (apps/api owns
// reading config files) and composes it with the in-lib registries. No I/O, no
// invented identifiers, no fixtures.

export type GovernedSemanticNodeKind =
  | 'objective'
  | 'metric'
  | 'target_family'
  | 'lever_family'
  | 'module'
  | 'evidence'
  | 'rollup_formula'
  | 'composition_formula';

export type GovernedSemanticNode = Readonly<{
  /** `<kind>:<key>` — unique within the graph. */
  id: string;
  kind: GovernedSemanticNodeKind;
  /** The identifier the node carries inside its own governed registry. */
  key: string;
  label: string;
  /** The governed source (registry row / config file entry) this node was read from. */
  sourceRef: string;
}>;

export type GovernedSemanticEdgeKind =
  | 'measured_by' // objective -> metric (identity-resolved, never guessed)
  | 'governed_by_family' // metric -> target_family
  | 'decomposes_to' // objective -> lever_family
  | 'served_by' // lever_family -> module
  | 'requires_evidence' // module -> evidence
  | 'aggregates_via' // metric -> rollup_formula
  | 'composed_via' // metric -> composition_formula (the formula that DEFINES it)
  | 'overlaps'; // lever_family <-> lever_family (declared pair, sorted)

export type GovernedSemanticEdge = Readonly<{
  from: string;
  to: string;
  kind: GovernedSemanticEdgeKind;
  /** The governed source that DECLARES this relationship. */
  sourceRef: string;
  attributes?: Readonly<Record<string, string>>;
  /**
   * The declaring mapping when the relationship holds PER STACK (served_by: a
   * lever node is shared, but which overlay binds it is declared per mapping) —
   * lineage filters on this so one objective's bindings are never attributed to
   * another objective that decomposes to the same lever through different overlays.
   */
  context?: Readonly<{ objectiveMetricId: string; stackId: string }>;
}>;

/** An honest, named gap: a hop the governed sources do not (yet) id-link. */
export type GovernedSemanticUnlinked = Readonly<{
  subjectId: string;
  hop: GovernedSemanticEdgeKind;
  reason: string;
  /**
   * The declaring context when the gap belongs to ONE objective's stack (a shared
   * lever node can carry per-stack gaps from several objectives) — lineage filters
   * on this so one objective's incomplete stack is never attributed to another's.
   */
  context?: Readonly<{ objectiveMetricId: string; stackId: string }>;
}>;

/** The ONE schema-version authority for this projection — import, never restate. */
export const GOVERNED_SEMANTIC_GRAPH_SCHEMA_VERSION = 'governed_semantic_graph_v1' as const;

export type GovernedSemanticGraph = Readonly<{
  schemaVersion: typeof GOVERNED_SEMANTIC_GRAPH_SCHEMA_VERSION;
  nodes: readonly GovernedSemanticNode[];
  edges: readonly GovernedSemanticEdge[];
  unlinked: readonly GovernedSemanticUnlinked[];
}>;

const nodeId = (kind: GovernedSemanticNodeKind, key: string): string => `${kind}:${key}`;

export function projectGovernedSemanticGraph(
  config: LoadedObjectiveTaxonomyConfig,
): GovernedSemanticGraph {
  const nodes = new Map<string, GovernedSemanticNode>();
  const edges = new Map<string, GovernedSemanticEdge>();
  const unlinked = new Map<string, GovernedSemanticUnlinked>();

  const addNode = (node: GovernedSemanticNode): string => {
    if (!nodes.has(node.id)) nodes.set(node.id, node);
    return node.id;
  };
  const addEdge = (edge: GovernedSemanticEdge): void => {
    const contextKey = edge.context
      ? `${edge.context.objectiveMetricId}|${edge.context.stackId}`
      : '';
    const key = `${edge.kind}|${edge.from}|${edge.to}|${edge.sourceRef}|${contextKey}`;
    if (!edges.has(key)) edges.set(key, edge);
  };
  // Deduped: the same gap reached through two mappings (both industries decompose the
  // same objective) is ONE fact, not two rows — consumers count gaps.
  const addUnlinked = (gap: GovernedSemanticUnlinked): void => {
    const contextKey = gap.context
      ? `${gap.context.objectiveMetricId}|${gap.context.stackId}`
      : '';
    const key = `${gap.subjectId}|${gap.hop}|${gap.reason}|${contextKey}`;
    if (!unlinked.has(key)) unlinked.set(key, gap);
  };

  // Lever-family definitions are resolved PER MAPPING from that mapping's DECLARED
  // overlay stack (core, then its industry overlays in declared order), mirroring the
  // resolver's precedence exactly — a global map over whatever overlays happen to be
  // loaded would let an unrelated overlay's definition leak into another stack's
  // nodes. Tenant taxonomy overlays are deliberately excluded: this is the
  // tenant-invariant static-authority catalog (see SCOPE above).
  type LeverFamilyDefinition = Readonly<{ label: string; sourceRef: string }>;
  const familyDefinitionsForMapping = (
    mapping: LoadedObjectiveTaxonomyConfig['map']['mappings'][number],
    objectiveNodeId: string,
  ): Map<string, LeverFamilyDefinition> => {
    const definitions = new Map<string, LeverFamilyDefinition>();
    const overlayIds = [mapping.coreTaxonomyOverlayId, ...mapping.industryTaxonomyOverlayIds];
    for (const overlayId of overlayIds) {
      const overlay = config.taxonomyOverlays[overlayId];
      if (!overlay) {
        addUnlinked({
          subjectId: objectiveNodeId,
          hop: 'decomposes_to',
          reason: `taxonomy_overlay_missing:${overlayId}`,
        });
        continue;
      }
      for (const family of overlay.leverFamilies) {
        definitions.set(family.id, {
          label: family.label,
          sourceRef: `taxonomy-overlay:${overlay.overlayId}@${overlay.version}`,
        });
      }
    }
    return definitions;
  };
  // A lever-family node is shared across stacks; if two stacks resolve the SAME id to
  // DIFFERENT governed definitions, the divergence is recorded — never silently
  // last-writer-wins (no such divergence ships today; conformance pins that).
  const addLeverFamilyNode = (
    leverFamilyId: string,
    definition: LeverFamilyDefinition,
  ): string => {
    const id = nodeId('lever_family', leverFamilyId);
    const existing = nodes.get(id);
    if (!existing) {
      nodes.set(id, {
        id,
        kind: 'lever_family',
        key: leverFamilyId,
        label: definition.label,
        sourceRef: definition.sourceRef,
      });
    } else if (
      existing.label !== definition.label ||
      existing.sourceRef !== definition.sourceRef
    ) {
      addUnlinked({
        subjectId: id,
        hop: 'decomposes_to',
        reason: `lever_family_definition_diverges_across_stacks:${leverFamilyId}`,
      });
    }
    return id;
  };
  // The registry version the identity translation selected per metric node — the
  // formula layer must project from THAT version only, never a union of versions.
  const metricRegistryVersionByKey = new Map<string, string>();

  for (const mapping of config.map.mappings) {
    const objectiveId = addNode({
      id: nodeId('objective', mapping.objectiveMetricId),
      kind: 'objective',
      key: mapping.objectiveMetricId,
      label: mapping.objectiveMetricId,
      sourceRef: `objective-taxonomy-map:${mapping.stackId}`,
    });

    const leverFamilyDefinitions = familyDefinitionsForMapping(mapping, objectiveId);

    // objective -> metric: through the ONE shared identity translation (Phase 4).
    const identity = resolveObjectiveMetricIdentity(mapping.objectiveMetricId);
    if (identity.status === 'resolved') {
      metricRegistryVersionByKey.set(identity.entry.metricKey, identity.registryVersion);
      const metricNodeId = addNode({
        id: nodeId('metric', identity.entry.metricKey),
        kind: 'metric',
        key: identity.entry.metricKey,
        label: identity.entry.label,
        sourceRef: `metric-registry:${identity.registryVersion}:${identity.entry.metricKey}`,
      });
      addEdge({
        from: objectiveId,
        to: metricNodeId,
        kind: 'measured_by',
        sourceRef: `objective-metric-identity:${identity.lookupKey}@${identity.registryVersion}`,
      });
    } else {
      addUnlinked({
        subjectId: objectiveId,
        hop: 'measured_by',
        reason: identity.reason,
      });
    }

    // objective -> lever_family: the map's curated decomposition. Gaps are recorded
    // against the OBJECTIVE node (which the graph always projects), so a broken hop
    // surfaces in that objective's lineage — a gap keyed to a node that was never
    // projected would be invisible to lineage consumers.
    for (const leverFamilyId of mapping.resolvedLeverFamilyIds) {
      const definition = leverFamilyDefinitions.get(leverFamilyId);
      if (!definition) {
        addUnlinked({
          subjectId: objectiveId,
          hop: 'decomposes_to',
          reason: `lever_family_not_defined_in_any_overlay:${leverFamilyId}`,
        });
        continue;
      }
      const leverNodeId = addLeverFamilyNode(leverFamilyId, definition);
      addEdge({
        from: objectiveId,
        to: leverNodeId,
        kind: 'decomposes_to',
        sourceRef: `objective-taxonomy-map:${mapping.stackId}`,
      });
    }

    // lever_family -> module -> evidence: the binding overlays this mapping declares.
    const boundFamiliesInMapping = new Set<string>();
    for (const overlayId of mapping.moduleBindingOverlayIds) {
      const overlay = config.moduleBindingOverlays[overlayId];
      if (!overlay) {
        addUnlinked({
          subjectId: objectiveId,
          hop: 'served_by',
          reason: `module_binding_overlay_missing:${overlayId}`,
        });
        continue;
      }
      for (const binding of overlay.bindings) {
        const bindingSourceRef = `module-binding:${binding.bindingId}@${overlay.version}`;
        const definition = leverFamilyDefinitions.get(binding.leverFamilyId);
        if (!definition) {
          // Keyed to the objective (always projected) so the gap reaches lineage.
          addUnlinked({
            subjectId: objectiveId,
            hop: 'served_by',
            reason: `binding_lever_family_not_defined_in_any_overlay:${binding.bindingId}`,
          });
          continue;
        }
        const leverNodeId = addLeverFamilyNode(binding.leverFamilyId, definition);
        boundFamiliesInMapping.add(binding.leverFamilyId);
        for (const moduleId of binding.supportedModuleIds) {
          const moduleNodeId = addNode({
            id: nodeId('module', moduleId),
            kind: 'module',
            key: moduleId,
            label: moduleId,
            sourceRef: bindingSourceRef,
          });
          addEdge({
            from: leverNodeId,
            to: moduleNodeId,
            kind: 'served_by',
            sourceRef: bindingSourceRef,
            context: {
              objectiveMetricId: mapping.objectiveMetricId,
              stackId: mapping.stackId,
            },
          });
          for (const evidence of binding.requiredEvidence) {
            // The evidence NODE is identity only: shipped bindings reuse one
            // evidenceId across rows with differing prose and source refs, so a
            // node labeled with one row's description would misattribute the
            // others. Per-declaration provenance (description, sourceClass,
            // freshness, declaring row) lives on each requires_evidence EDGE;
            // the node's sourceRef records the first declaring row only.
            const evidenceNodeId = addNode({
              id: nodeId('evidence', evidence.evidenceId),
              kind: 'evidence',
              key: evidence.evidenceId,
              label: evidence.evidenceId,
              sourceRef: bindingSourceRef,
            });
            addEdge({
              from: moduleNodeId,
              to: evidenceNodeId,
              kind: 'requires_evidence',
              sourceRef: bindingSourceRef,
              attributes: {
                description: evidence.description,
                sourceClass: evidence.sourceClass,
                requiredFreshness: evidence.requiredFreshness,
              },
              // Binding-declared evidence is a per-mapping requirement, exactly
              // like the served_by edge it rides on; contract-attested evidence
              // (added below from the registry) stays global by design.
              context: {
                objectiveMetricId: mapping.objectiveMetricId,
                stackId: mapping.stackId,
              },
            });
          }
        }
      }
    }

    // Binding coverage, per stack: a lever family the mapping DECOMPOSES to but no
    // declared binding overlay SERVES is a broken served_by hop for that stack —
    // recorded against the (projected) lever node with the stack named, so lineage
    // consumers can distinguish an unbound family from a bound one. The same family
    // may legitimately be bound in one stack and unbound in another (retail
    // delayering is unbound while logistics binds it) — the stack in the reason
    // keeps both facts visible.
    for (const leverFamilyId of mapping.resolvedLeverFamilyIds) {
      if (boundFamiliesInMapping.has(leverFamilyId)) continue;
      if (!leverFamilyDefinitions.has(leverFamilyId)) continue; // already a decomposes_to gap
      addUnlinked({
        subjectId: nodeId('lever_family', leverFamilyId),
        hop: 'served_by',
        reason: `no_module_binding_in_stack:${mapping.stackId}`,
        context: {
          objectiveMetricId: mapping.objectiveMetricId,
          stackId: mapping.stackId,
        },
      });
    }
  }

  // metric -> target_family and metric -> rollup_formula for every metric the
  // objectives reached. Join is on CANONICAL key via the version-scoped resolver —
  // the same alias-safe authority the Phase 4 conformance pins. The target join map
  // is built ONCE (the registries are immutable module constants).
  const targetByCanonicalKey = new Map(
    TARGET_METRIC_REGISTRY.flatMap((entry) => {
      const resolved = resolveMetricTargetReference({
        metricId: entry.metricId,
        registryVersion: TARGET_METRIC_REGISTRY_VERSION,
      });
      return resolved ? [[resolved.metricKey, entry] as const] : [];
    }),
  );
  const metricNodes = [...nodes.values()].filter((candidate) => candidate.kind === 'metric');
  for (const node of metricNodes) {
    const target = targetByCanonicalKey.get(node.key);
    if (target) {
      const targetSourceRef = [
        'target-metric-registry',
        TARGET_METRIC_REGISTRY_VERSION,
        target.metricId,
      ].join(':');
      const targetNodeId = addNode({
        id: nodeId('target_family', target.metricId),
        kind: 'target_family',
        key: target.metricId,
        label: target.label,
        sourceRef: targetSourceRef,
      });
      addEdge({
        from: node.id,
        to: targetNodeId,
        kind: 'governed_by_family',
        sourceRef: targetSourceRef,
        attributes: { metricFamily: target.metricFamily },
      });
    } else {
      addUnlinked({
        subjectId: node.id,
        hop: 'governed_by_family',
        reason: `no_target_metric_registry_entry:${node.key}`,
      });
    }

    for (const definition of GOVERNED_METRIC_REGISTRY_COMPILED_DEFINITIONS) {
      if (definition.metricKey !== node.key) continue;
      // Formulas project from the registry version the IDENTITY translation selected
      // for this metric — attaching every version's rules to one node would merge
      // (and today duplicate) rule sets across versions.
      if (definition.registryVersion !== metricRegistryVersionByKey.get(node.key)) continue;
      const definitionSourceRef = [
        'metric-registry',
        definition.registryVersion ?? 'unversioned',
        definition.metricKey,
      ].join(':');
      for (const rule of definition.aggregationRules) {
        const formulaNodeId = addNode({
          id: nodeId('rollup_formula', rule.ruleFormulaId),
          kind: 'rollup_formula',
          key: rule.ruleFormulaId,
          label: rule.ruleFormulaId,
          sourceRef: definitionSourceRef,
        });
        addEdge({
          from: node.id,
          to: formulaNodeId,
          kind: 'aggregates_via',
          // Full rule identity in the ref: a dimension carries MULTIPLE rollups
          // (business_line: reported_segment AND enterprise), and each governed
          // rule must remain its own attributable edge, never deduped away.
          sourceRef: `${definitionSourceRef}#aggregationRules.${rule.dimension}.${rule.rollup}`,
          attributes: { dimension: rule.dimension, rollup: rule.rollup },
        });
      }

      // The canonical COMPOSITION formula — the governed formula that DEFINES a
      // derived metric (e.g. ebitda_from_operating_income_v1) — is a different fact
      // from dimensional rollup rules and is projected as its own layer. Input
      // metric keys ride the edge attributes: an input need not be a projected
      // node (no objective decomposes to depreciation), and edge endpoints must
      // always exist.
      if (definition.composedFrom) {
        const composition = definition.composedFrom;
        const compositionNodeId = addNode({
          id: nodeId(
            'composition_formula',
            `${composition.formulaId}@${composition.formulaVersion}`,
          ),
          kind: 'composition_formula',
          key: composition.formulaId,
          label: composition.formulaId,
          sourceRef: `${definitionSourceRef}#composedFrom`,
        });
        addEdge({
          from: node.id,
          to: compositionNodeId,
          kind: 'composed_via',
          sourceRef: `${definitionSourceRef}#composedFrom`,
          attributes: {
            formulaVersion: composition.formulaVersion,
            inputMetricKeys: composition.inputMetricKeys.join(','),
          },
        });
      }
    }
  }

  // module -> evidence from the opt-in evidence contract registry: a second,
  // independently governed provenance class for the same hop (contract-attested
  // artifact identity vs binding-declared requirement). Only modules the graph
  // reached — the projection never invents reach.
  for (const node of [...nodes.values()].filter((candidate) => candidate.kind === 'module')) {
    const contract =
      MODULE_EVIDENCE_CONTRACT_REGISTRY[
        node.key as keyof typeof MODULE_EVIDENCE_CONTRACT_REGISTRY
      ];
    if (!contract) continue;
    for (const entry of Object.values(contract.entries)) {
      const evidenceNodeId = addNode({
        id: nodeId('evidence', entry.evidenceKey),
        kind: 'evidence',
        key: entry.evidenceKey,
        label: entry.evidenceKey,
        sourceRef: `module-evidence-contract:${contract.moduleId}`,
      });
      addEdge({
        from: node.id,
        to: evidenceNodeId,
        kind: 'requires_evidence',
        sourceRef: `module-evidence-contract:${contract.moduleId}:${entry.evidenceKey}`,
        attributes: {
          sourceClass: entry.sourceClass,
          contextKind: entry.contextKind,
          schemaVersion: entry.schemaVersion,
        },
      });
    }
  }

  // lever_family overlap declarations (de-confliction law). Edges attach only when
  // BOTH families are projected taxonomy lever families. The overlap registry is
  // canonical-layer and legitimately governs claim families that exist at the
  // value-bridge layer without a taxonomy decomposition (e.g. org_cost — the fence
  // keeping unproven org-cost claims out of the certified floor). A skipped
  // declaration is recorded against its PROJECTED endpoint (deduped per missing
  // counterpart), so any lineage containing that family surfaces the undrawn
  // overlap constraint — a gap keyed only to the absent family would be invisible
  // to lineage consumers, who could mistake the lineage for receipt-complete.
  const overlapOnlyFamilies = new Set<string>();
  for (const declaration of listLeverOverlapDeclarations()) {
    const [familyA, familyB] = [declaration.familyA, declaration.familyB].sort();
    const fromId = nodeId('lever_family', familyA);
    const toId = nodeId('lever_family', familyB);
    const missing = [familyA, familyB].filter(
      (family) => !nodes.has(nodeId('lever_family', family)),
    );
    if (missing.length > 0) {
      for (const family of missing) overlapOnlyFamilies.add(family);
      const present = [familyA, familyB].filter((family) => !missing.includes(family));
      for (const family of present) {
        for (const counterpart of missing) {
          addUnlinked({
            subjectId: nodeId('lever_family', family),
            hop: 'overlaps',
            reason: `overlap_counterpart_not_in_taxonomy_decomposition:${counterpart}`,
          });
        }
      }
      continue;
    }
    addEdge({
      from: fromId,
      to: toId,
      kind: 'overlaps',
      // Pair-specific ref plus the declaration's own economic basis: an overlap
      // ruling must stay attributable and reproducible from the graph alone.
      sourceRef: `lever-overlap-registry:${familyA}|${familyB}`,
      attributes: {
        relation: declaration.relation,
        evidenceRequirement: declaration.evidenceRequirement,
        basis: declaration.basis,
      },
    });
  }

  for (const family of [...overlapOnlyFamilies].sort()) {
    addUnlinked({
      subjectId: nodeId('lever_family', family),
      hop: 'overlaps',
      reason: `overlap_family_not_in_taxonomy_decomposition:${family}`,
    });
  }

  return {
    schemaVersion: GOVERNED_SEMANTIC_GRAPH_SCHEMA_VERSION,
    nodes: [...nodes.values()],
    edges: [...edges.values()],
    unlinked: [...unlinked.values()],
  };
}

// ---------------------------------------------------------------------------
// Lineage query — the question the graph exists to answer:
// "what moves this metric, through which modules, on what evidence?"
// ---------------------------------------------------------------------------

export type GovernedMetricLineage = Readonly<{
  metricKey: string;
  metricNodeId: string;
  objectives: readonly Readonly<{ objectiveMetricId: string; sourceRef: string }>[];
  targetFamilies: readonly Readonly<{
    targetMetricId: string;
    /** The economic family from the governing edge — family semantics ride the answer. */
    metricFamily: string;
    sourceRef: string;
  }>[];
  leverFamilies: readonly Readonly<{
    leverFamilyId: string;
    viaObjective: string;
    sourceRef: string;
  }>[];
  modules: readonly Readonly<{
    moduleId: string;
    viaLeverFamily: string;
    sourceRef: string;
  }>[];
  evidence: readonly Readonly<{
    evidenceId: string;
    viaModule: string;
    sourceRef: string;
    /** Declaration-specific facts from the declaring edge (description, sourceClass, …). */
    attributes: Readonly<Record<string, string>>;
  }>[];
  rollupFormulas: readonly Readonly<{ formulaId: string; sourceRef: string }>[];
  /**
   * Declared overlap relations among THIS lineage's lever families — which levers
   * require de-confliction receipts before their contributions may be aggregated.
   */
  leverOverlaps: readonly Readonly<{
    familyA: string;
    familyB: string;
    relation: string;
    evidenceRequirement: string;
    /** The declaration's recorded economic basis — the WHY of the ruling. */
    basis: string;
    sourceRef: string;
  }>[];
  /** The governed formula(s) that DEFINE the metric, with declared input keys. */
  compositionFormulas: readonly Readonly<{
    formulaId: string;
    formulaVersion: string;
    inputMetricKeys: readonly string[];
    sourceRef: string;
  }>[];
  /**
   * Gaps touching any node in THIS lineage — surfaced with the answer, never
   * hidden. Context-scoped gaps (a per-stack row on a shared lever node) are
   * included only when their declaring objective is part of this lineage.
   */
  unlinked: readonly GovernedSemanticUnlinked[];
}>;

/**
 * Walk the graph from a metric (canonical key, registered alias, or objective URN —
 * identity goes through the shared Phase 4 resolver, never string matching).
 * Returns undefined when the metric has no governed identity: the honest empty state.
 */
export function governedMetricLineage(
  graph: GovernedSemanticGraph,
  metricIdOrUrn: string,
): GovernedMetricLineage | undefined {
  const identity = resolveObjectiveMetricIdentity(metricIdOrUrn);
  if (identity.status !== 'resolved') return undefined;
  const metricNodeId = nodeId('metric', identity.entry.metricKey);
  if (!graph.nodes.some((node) => node.id === metricNodeId)) return undefined;

  const keyOf = (id: string): string => id.slice(id.indexOf(':') + 1);

  const objectives = graph.edges
    .filter((edge) => edge.kind === 'measured_by' && edge.to === metricNodeId)
    .map((edge) => ({ objectiveMetricId: keyOf(edge.from), sourceRef: edge.sourceRef }));

  const targetFamilies = graph.edges
    .filter((edge) => edge.kind === 'governed_by_family' && edge.from === metricNodeId)
    .map((edge) => ({
      targetMetricId: keyOf(edge.to),
      metricFamily: edge.attributes?.metricFamily ?? '',
      sourceRef: edge.sourceRef,
    }));

  const objectiveIds = new Set(
    objectives.map((entry) => nodeId('objective', entry.objectiveMetricId)),
  );
  const leverFamilies = graph.edges
    .filter((edge) => edge.kind === 'decomposes_to' && objectiveIds.has(edge.from))
    .map((edge) => ({
      leverFamilyId: keyOf(edge.to),
      viaObjective: keyOf(edge.from),
      sourceRef: edge.sourceRef,
    }));

  const leverIds = new Set(
    leverFamilies.map((entry) => nodeId('lever_family', entry.leverFamilyId)),
  );
  const lineageObjectiveMetricIds = new Set(
    objectives.map((entry) => entry.objectiveMetricId),
  );
  // served_by edges are declared per mapping; only edges declared by one of THIS
  // lineage's objectives count — a shared lever must not import another objective's
  // module bindings. Rows are deduped because the same binding may serve several of
  // one objective's stacks.
  const moduleRows = new Map<
    string,
    { moduleId: string; viaLeverFamily: string; sourceRef: string }
  >();
  for (const edge of graph.edges) {
    if (edge.kind !== 'served_by' || !leverIds.has(edge.from)) continue;
    if (edge.context && !lineageObjectiveMetricIds.has(edge.context.objectiveMetricId)) continue;
    const row = {
      moduleId: keyOf(edge.to),
      viaLeverFamily: keyOf(edge.from),
      sourceRef: edge.sourceRef,
    };
    moduleRows.set(`${row.moduleId}|${row.viaLeverFamily}|${row.sourceRef}`, row);
  }
  const modules = [...moduleRows.values()];

  const moduleIds = new Set(modules.map((entry) => nodeId('module', entry.moduleId)));
  // Binding-declared evidence edges are per-mapping (context-scoped, same law as
  // served_by); module-contract evidence edges are global. Rows dedupe because one
  // requirement may be declared by several of this lineage's stacks.
  const evidenceRows = new Map<
    string,
    {
      evidenceId: string;
      viaModule: string;
      sourceRef: string;
      attributes: Readonly<Record<string, string>>;
    }
  >();
  for (const edge of graph.edges) {
    if (edge.kind !== 'requires_evidence' || !moduleIds.has(edge.from)) continue;
    if (edge.context && !lineageObjectiveMetricIds.has(edge.context.objectiveMetricId)) continue;
    const row = {
      evidenceId: keyOf(edge.to),
      viaModule: keyOf(edge.from),
      sourceRef: edge.sourceRef,
      // Declaration-specific facts (description/sourceClass/freshness for binding
      // evidence; contextKind/schemaVersion for contract evidence) ride the answer
      // — "on what evidence" must be attributable without reopening the registries.
      attributes: edge.attributes ?? {},
    };
    evidenceRows.set(`${row.evidenceId}|${row.viaModule}|${row.sourceRef}`, row);
  }
  const evidence = [...evidenceRows.values()];

  const leverOverlaps = graph.edges
    .filter(
      (edge) =>
        edge.kind === 'overlaps' && leverIds.has(edge.from) && leverIds.has(edge.to),
    )
    .map((edge) => ({
      familyA: keyOf(edge.from),
      familyB: keyOf(edge.to),
      relation: edge.attributes?.relation ?? '',
      evidenceRequirement: edge.attributes?.evidenceRequirement ?? '',
      basis: edge.attributes?.basis ?? '',
      sourceRef: edge.sourceRef,
    }));

  const rollupFormulas = graph.edges
    .filter((edge) => edge.kind === 'aggregates_via' && edge.from === metricNodeId)
    .map((edge) => ({ formulaId: keyOf(edge.to), sourceRef: edge.sourceRef }));

  const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));
  const compositionFormulas = graph.edges
    .filter((edge) => edge.kind === 'composed_via' && edge.from === metricNodeId)
    .map((edge) => ({
      formulaId: nodeById.get(edge.to)?.key ?? keyOf(edge.to),
      formulaVersion: edge.attributes?.formulaVersion ?? '',
      inputMetricKeys: (edge.attributes?.inputMetricKeys ?? '')
        .split(',')
        .filter((key) => key.length > 0),
      sourceRef: edge.sourceRef,
    }));

  const lineageNodeIds = new Set<string>([
    metricNodeId,
    ...objectiveIds,
    ...leverIds,
    ...moduleIds,
  ]);
  const unlinkedInLineage = graph.unlinked.filter(
    (gap) =>
      lineageNodeIds.has(gap.subjectId) &&
      // A context-scoped gap belongs to ONE objective's stack; on a shared lever
      // node it must not leak into another objective's lineage.
      (!gap.context || lineageObjectiveMetricIds.has(gap.context.objectiveMetricId)),
  );

  return {
    metricKey: identity.entry.metricKey,
    metricNodeId,
    objectives,
    targetFamilies,
    leverFamilies,
    modules,
    evidence,
    rollupFormulas,
    leverOverlaps,
    compositionFormulas,
    unlinked: unlinkedInLineage,
  };
}
